import { useParams, useNavigate } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { useState } from 'react';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useCheckRun, useSubmitRun } from '../lib/queries.js';
import { SyncBanner } from '../components/SyncBanner.js';
import { useOutbox, pendingForRun } from '../lib/offline/outbox.js';
import { NOT_ON_PHONE } from '../lib/offline/messages.js';
import { PageShell } from '../components/PageShell.js';
import { StatusPill } from '../components/StatusPill.js';
import { Icon } from '../components/Icon.js';
import type { CheckStatus } from '@keetcheck/shared';
import { ApiError } from '../lib/api.js';

export function RunSummaryPage() {
  const { runId } = useParams<{ runId: string }>();
  const nav = useNavigate();
  const back = useBack(`/checks/${runId}`);
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const { data, isLoading } = useCheckRun(orgId, runId ?? '');
  const submitMut = useSubmitRun(orgId);

  const [note, setNote] = useState('');

  const items = data?.items ?? [];
  const issues = items.filter((i) =>
    i.status && i.status !== 'ok' && i.status !== 'rectified',
  );
  const missing = items.filter((i) => !i.status);
  // The server refuses issues with no note (spaces don't count) and no photo;
  // say which ones here rather than after tapping Submit.
  const noEvidence = new Set(issues.filter((i) => !i.note?.trim() && !i.photoCount).map((i) => i.id));
  // Everything on this phone must reach the server first — it checks again.
  const outbox = useOutbox();
  const waiting = pendingForRun(outbox, runId ?? '');
  const synced = waiting === 0 && outbox.online;
  const canSubmit = synced && missing.length === 0 && noEvidence.size === 0 && note.trim().length > 0 && data?.status === 'draft';

  function handleSubmit() {
    if (!canSubmit) return;
    submitMut.mutate(
      { runId: runId!, submissionNote: note.trim() },
      // back to the checklist underneath, not a second copy of it on top.
      // A 409 means it's already submitted (a double tap, or someone else):
      // the toast says so, and there's nothing left to do here either.
      { onSuccess: back, onError: (e) => { if (e instanceof ApiError && e.status === 409) back(); } },
    );
  }

  if (isLoading) return <PageShell noNav><Msg onBack={back}>{navigator.onLine ? 'Loading…' : NOT_ON_PHONE}</Msg></PageShell>;
  if (!data) return <PageShell noNav><Msg onBack={back}>Not found</Msg></PageShell>;

  return (
    <PageShell noNav>
      {/* header */}
      <div style={{
        padding: '12px 16px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10,
        position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        <button onClick={back} style={iconBtn} aria-label="Back">
          <Icon name="back" size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600 }}>
            {data.teamName ?? 'Any crew'}
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>Review & submit</div>
        </div>
      </div>

      <SyncBanner runId={runId ?? ''} />

      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg }}>

        {/* stats row */}
        <div style={{ display: 'flex', gap: 10, padding: '16px 16px 0' }}>
          {[
            { label: 'Total items', value: items.length, tone: 'muted' as const },
            { label: 'Issues', value: issues.length, tone: 'warn' as const },
            { label: 'Unchecked', value: missing.length, tone: missing.length > 0 ? 'danger' as const : 'muted' as const },
          ].map((stat) => (
            <div key={stat.label} style={{
              flex: 1, padding: '12px 10px', borderRadius: 10,
              background: theme.surface, border: `1px solid ${theme.border}`,
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: 22, fontWeight: 700,
                color: stat.tone !== 'muted' && (stat.value > 0) ? theme.status[stat.tone].fg : theme.text,
              }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* unchecked warning */}
        {missing.length > 0 && (
          <div style={{
            margin: '12px 16px 0',
            padding: '10px 14px',
            background: theme.status.danger.bg,
            borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon name="warning" size={16} style={{ color: theme.status.danger.fg, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: theme.status.danger.fg, fontWeight: 500 }}>
              {missing.length} item{missing.length > 1 ? 's' : ''} still unchecked — go back and complete them first.
            </span>
          </div>
        )}

        {noEvidence.size > 0 && (
          <div style={{
            margin: '12px 16px 0',
            padding: '10px 14px',
            background: theme.status.danger.bg,
            borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon name="warning" size={16} style={{ color: theme.status.danger.fg, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: theme.status.danger.fg, fontWeight: 500 }}>
              {noEvidence.size} issue{noEvidence.size > 1 ? 's need' : ' needs'} a note or photo — tap {noEvidence.size > 1 ? 'each' : 'it'} below to add one.
            </span>
          </div>
        )}

        {/* issues list */}
        {issues.length > 0 && (
          <div style={{ padding: '16px 16px 0' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.textDim, marginBottom: 8, letterSpacing: 0.2 }}>
              Issues to review
            </div>
            <div style={{
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 10, overflow: 'hidden',
            }}>
              {issues.map((item, i) => (
                <div
                  key={item.id}
                  onClick={() => nav(`/checks/${runId}/items/${item.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px',
                    borderBottom: i < issues.length - 1 ? `1px solid ${theme.border}` : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.equipmentName ?? 'Item'}
                    </div>
                    {noEvidence.has(item.id) ? (
                      <div style={{ fontSize: 12, color: theme.status.danger.fg, fontWeight: 600, marginTop: 2 }}>
                        Needs a note or photo
                      </div>
                    ) : item.note?.trim() && (
                      <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.note}
                      </div>
                    )}
                  </div>
                  <StatusPill status={item.status as CheckStatus} size="sm" />
                  <Icon name="chevron" size={14} style={{ color: theme.textMuted, flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* submission note */}
        <div style={{ padding: '16px 16px 0' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.textDim, marginBottom: 8, letterSpacing: 0.2 }}>
            Submission note <span style={{ color: theme.status.danger.dot }}>*</span>
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Brief summary of this check…"
            rows={4}
            style={{
              width: '100%', padding: '10px 12px',
              background: theme.surface, border: `1px solid ${theme.border}`,
              borderRadius: 10, resize: 'none', fontSize: 14,
              color: theme.text, fontFamily: theme.font,
              lineHeight: 1.5, boxSizing: 'border-box', outline: 'none',
            }}
          />
        </div>

        {/* submit */}
        <div style={{ padding: '16px 16px 36px' }}>
          {!synced && (
            <div style={{ fontSize: 13, color: theme.status.warn.fg, fontWeight: 600, marginBottom: 10 }}>
              {waiting
                ? `Submitting needs a connection: ${waiting} change${waiting > 1 ? 's are' : ' is'} still on this phone.`
                : 'Submitting needs a connection — you can submit once you have signal.'}
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitMut.isPending}
            style={{
              width: '100%', padding: '14px 0',
              background: canSubmit ? theme.accent : theme.surface2,
              color: canSubmit ? theme.accentInk : theme.textMuted,
              border: `1px solid ${canSubmit ? theme.accent : theme.border}`,
              borderRadius: 12, fontSize: 15, fontWeight: 700,
              cursor: canSubmit ? 'pointer' : 'default',
              fontFamily: theme.font,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {submitMut.isPending
              ? 'Submitting…'
              : <><Icon name="send" size={16} /> Submit check</>}
          </button>
        </div>
      </div>
    </PageShell>
  );
}

/** Loading / not found. The page hides the bottom nav, so keep a way out. */
function Msg({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
      {children}
      <div style={{ marginTop: 16 }}>
        <button onClick={onBack} style={{
          background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10,
          padding: '8px 16px', color: theme.textDim, fontFamily: theme.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>Go back</button>
      </div>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
